import React, { useEffect, useState } from "react";
import { Plus, Clock, Trash2, CalendarDays, Video, Timer, LayoutGrid, List } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import VideoMeetingModal, { genMeetingUrl } from "../common/VideoMeetingModal";
import {
  fetchAppointments, createAppointment, updateAppointmentApi, deleteAppointmentApi, fetchClients, formatDate,
} from "../../lib/api";
import { appointmentStatuses } from "../../mock";

function Countdown({ date, time }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const target = new Date(`${date} ${time}`).getTime();
  if (isNaN(target)) return null;
  const diff = target - now;

  if (diff <= -2 * 3600 * 1000) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
        <Timer className="h-3.5 w-3.5" /> Ended
      </span>
    );
  }
  if (diff <= 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
        </span>
        Live now
      </span>
    );
  }

  const totalMins = Math.floor(diff / 60000);
  const days = Math.floor(totalMins / 1440);
  const hours = Math.floor((totalMins % 1440) / 60);
  const mins = totalMins % 60;
  const secs = Math.floor((diff % 60000) / 1000);

  let label;
  if (days > 0) label = `in ${days}d ${hours}h`;
  else if (hours > 0) label = `in ${hours}h ${mins}m`;
  else label = `in ${mins}m ${String(secs).padStart(2, "0")}s`;

  const soon = diff <= 15 * 60 * 1000;
  const near = diff <= 60 * 60 * 1000;

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
        soon
          ? "text-amber-800 bg-amber-100 animate-pulse"
          : near
          ? "text-gold-dark bg-gold/15"
          : "text-navy bg-navy/5"
      }`}
    >
      <Timer className="h-3.5 w-3.5" />
      {soon ? "Starting soon · " : ""}
      {label}
    </span>
  );
}

const statusColor = (s) =>
  ({
    Pending: "bg-amber-100 text-amber-700",
    Confirmed: "bg-green-100 text-green-700",
    Completed: "bg-blue-100 text-blue-700",
    Cancelled: "bg-gray-100 text-gray-500",
  }[s] || "bg-gray-100 text-gray-600");

const apptTypes = [
  "Free Consultation",
  "Quarterly Review",
  "Retirement Strategy",
  "Credit Review",
  "Insurance Planning",
  "Follow-up",
];

export default function Appointments() {
  const [appts, setAppts] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [meeting, setMeeting] = useState({ open: false, url: "", title: "", name: "" });
  const [form, setForm] = useState({ clientName: "", type: "Free Consultation", date: "", time: "", notes: "", status: "Pending", addVideo: true });
  const [view, setView] = useState(() => localStorage.getItem("appt_view") || "grid");
  const [statusFilter, setStatusFilter] = useState("All");
  const [rangeFilter, setRangeFilter] = useState("all");
  const [sort, setSort] = useState({ key: "date", dir: "asc" });

  const setViewMode = (v) => { setView(v); localStorage.setItem("appt_view", v); };

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const refresh = async () => setAppts(await fetchAppointments());

  useEffect(() => {
    refresh();
    fetchClients().then(setClients);
  }, []);

  const handleAdd = async () => {
    if (!form.clientName || !form.date || !form.time) {
      toast.error("Client, date and time are required");
      return;
    }
    const { addVideo, ...rest } = form;
    await createAppointment({ ...rest, meetingUrl: addVideo ? genMeetingUrl() : null });
    await refresh();
    setOpen(false);
    setForm({ clientName: "", type: "Free Consultation", date: "", time: "", notes: "", status: "Pending", addVideo: true });
    toast.success(addVideo ? "Appointment scheduled with video meeting" : "Appointment scheduled");
  };

  const addVideoToAppt = async (a) => {
    const url = genMeetingUrl();
    await updateAppointmentApi(a.id, { meetingUrl: url });
    await refresh();
    toast.success("Video meeting added");
  };

  const joinMeeting = (a) =>
    setMeeting({ open: true, url: a.meetingUrl, title: `${a.type} · ${a.clientName}`, name: "RFL Coach" });

  const changeStatus = async (id, status) => {
    await updateAppointmentApi(id, { status });
    await refresh();
    toast.success(`Marked ${status}`);
  };

  const remove = async (id) => {
    await deleteAppointmentApi(id);
    await refresh();
    toast.success("Appointment removed");
  };

  const sorted = (() => {
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const now = new Date();
    const todayISO = toISO(now);
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const weekStart = toISO(monday);
    const weekEnd = toISO(sunday);

    const inRange = (a) => {
      if (rangeFilter === "today") return a.date === todayISO;
      if (rangeFilter === "week") return a.date >= weekStart && a.date <= weekEnd;
      if (rangeFilter === "upcoming") return a.date >= todayISO;
      return true;
    };

    const filtered = appts.filter((a) => (statusFilter === "All" || a.status === statusFilter) && inRange(a));
    const dir = sort.dir === "asc" ? 1 : -1;
    return filtered.sort((a, b) => {
      let av, bv;
      if (sort.key === "date") { av = `${a.date} ${a.time}`; bv = `${b.date} ${b.time}`; }
      else { av = (a[sort.key] || "").toString().toLowerCase(); bv = (b[sort.key] || "").toString().toLowerCase(); }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  })();
  const sortArrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ↑" : " ↓") : "");

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Appointments</h1>
          <p className="text-gray-500 text-sm mt-0.5">{appts.length} scheduled sessions</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-36 bg-white text-sm" data-testid="appt-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All statuses</SelectItem>
              {appointmentStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5" data-testid="appt-view-toggle">
            <button
              onClick={() => setViewMode("grid")}
              className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${view === "grid" ? "bg-navy text-white" : "text-gray-400 hover:text-navy"}`}
              title="Grid view"
              data-testid="appt-view-grid"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${view === "list" ? "bg-navy text-white" : "text-gray-400 hover:text-navy"}`}
              title="List view"
              data-testid="appt-view-list"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          <Button onClick={() => setOpen(true)} className="bg-navy hover:bg-navy-light text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Schedule
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="appt-range-filter">
        {[
          { v: "all", label: "All" },
          { v: "today", label: "Today" },
          { v: "week", label: "This Week" },
          { v: "upcoming", label: "Upcoming" },
        ].map((r) => (
          <button
            key={r.v}
            onClick={() => setRangeFilter(r.v)}
            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${rangeFilter === r.v ? "bg-gold text-navy" : "bg-white text-gray-600 hover:bg-gray-100"}`}
            data-testid={`range-${r.v}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {view === "list" ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="appt-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream">
                <tr className="text-left text-gray-500">
                  <th onClick={() => toggleSort("clientName")} className="px-5 py-3.5 font-medium cursor-pointer select-none hover:text-navy" data-testid="sort-client">Client{sortArrow("clientName")}</th>
                  <th onClick={() => toggleSort("type")} className="px-5 py-3.5 font-medium hidden md:table-cell cursor-pointer select-none hover:text-navy" data-testid="sort-type">Type{sortArrow("type")}</th>
                  <th onClick={() => toggleSort("date")} className="px-5 py-3.5 font-medium cursor-pointer select-none hover:text-navy" data-testid="sort-date">Date &amp; Time{sortArrow("date")}</th>
                  <th onClick={() => toggleSort("status")} className="px-5 py-3.5 font-medium cursor-pointer select-none hover:text-navy" data-testid="sort-status">Status{sortArrow("status")}</th>
                  <th className="px-5 py-3.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => (
                  <tr key={a.id} className="border-t border-gray-50 hover:bg-cream/40 transition-colors" data-testid={`appt-row-${a.id}`}>
                    <td className="px-5 py-3.5 font-medium text-navy">{a.clientName}</td>
                    <td className="px-5 py-3.5 text-gray-600 hidden md:table-cell">{a.type}</td>
                    <td className="px-5 py-3.5 text-gray-600">{formatDate(a.date)} · {a.time}</td>
                    <td className="px-5 py-3.5">
                      <Select value={a.status} onValueChange={(v) => changeStatus(a.id, v)}>
                        <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>{appointmentStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        {a.meetingUrl ? (
                          <button onClick={() => joinMeeting(a)} title="Join video call" className="inline-flex items-center gap-1 text-xs font-medium text-gold-dark hover:bg-gold/15 px-2 py-1.5 rounded-md transition-colors">
                            <Video className="h-3.5 w-3.5" /> Join
                          </button>
                        ) : (
                          <button onClick={() => addVideoToAppt(a)} title="Add video meeting" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-navy px-2 py-1.5 rounded-md transition-colors">
                            <Video className="h-3.5 w-3.5" /> Add
                          </button>
                        )}
                        <button onClick={() => remove(a.id)} title="Delete" className="h-8 w-8 flex items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan="5" className="px-5 py-12 text-center text-gray-400">No appointments scheduled.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {sorted.map((a) => (
          <div key={a.id} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-lg bg-cream flex items-center justify-center">
                  <CalendarDays className="h-5 w-5 text-gold-dark" />
                </div>
                <div>
                  <div className="font-semibold text-navy">{a.clientName}</div>
                  <div className="text-xs text-gray-400">{a.type}</div>
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColor(a.status)}`}>
                {a.status}
              </span>
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-gray-400" /> {formatDate(a.date)}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-gray-400" /> {a.time}</span>
            </div>
            <div className="mt-2.5">
              <Countdown date={a.date} time={a.time} />
            </div>
            {a.notes && <p className="mt-3 text-sm text-gray-500">{a.notes}</p>}
            {a.meetingUrl ? (
              <button onClick={() => joinMeeting(a)} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gold/15 hover:bg-gold/25 text-gold-dark font-medium text-sm py-2.5 transition-colors">
                <Video className="h-4 w-4" /> Join Video Call
              </button>
            ) : (
              <button onClick={() => addVideoToAppt(a)} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 hover:border-navy/30 text-gray-500 hover:text-navy text-sm py-2.5 transition-colors">
                <Video className="h-4 w-4" /> Add video meeting
              </button>
            )}
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
              <Select value={a.status} onValueChange={(v) => changeStatus(a.id, v)}>
                <SelectTrigger className="h-9 text-sm flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>{appointmentStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => remove(a.id)} className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {sorted.length === 0 && (
          <div className="col-span-full bg-white rounded-xl p-12 text-center text-gray-400 border border-dashed border-gray-200">
            No appointments scheduled.
          </div>
        )}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy">Schedule Appointment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Select value={form.clientName} onValueChange={(v) => setForm({ ...form, clientName: v })}>
              <SelectTrigger><SelectValue placeholder="Select a client *" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                {clients.length === 0 && <SelectItem value="__none" disabled>No clients yet</SelectItem>}
              </SelectContent>
            </Select>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{apptTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Date</label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="appt-date-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Time</label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="appt-time-input" />
              </div>
            </div>
            <Textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            <label className="flex items-center justify-between gap-2 text-sm text-gray-700 bg-cream rounded-lg px-3 py-2.5">
              <span className="flex items-center gap-2"><Video className="h-4 w-4 text-gold-dark" /> Add a video meeting link</span>
              <Switch checked={form.addVideo} onCheckedChange={(v) => setForm({ ...form, addVideo: v })} />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-navy hover:bg-navy-light text-white">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VideoMeetingModal
        open={meeting.open}
        onOpenChange={(o) => setMeeting((m) => ({ ...m, open: o }))}
        url={meeting.url}
        title={meeting.title}
        name={meeting.name}
      />
    </div>
  );
}
